// src/pages/PagesEnseignants/ListingAdherents/ManageGroupModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import {
  addAdherentToGroup,
  removeAdherentFromGroup,
} from "../../../services/adherentServices";
import { toast } from "react-toastify";

export default function ManageGroupModal({
  isOpen,
  onClose,
  adherentId,
  groups = [], // liste complète des groupes (déjà fournie par le parent)
  adherentGroups = [], // groupes actuels de l’adhérent
  refreshData, // callback pour recharger les données en sortie
}) {
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const nameToId = useMemo(() => {
    const m = new Map();
    (groups || []).forEach((g) => {
      const k = String(g?.nom || "")
        .trim()
        .toLowerCase();
      if (k) m.set(k, String(g.id));
    });
    return m;
  }, [groups]);

  // Init sélection à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    setFilter("");

    const initial = new Set(
      (adherentGroups || [])
        .map((g) => {
          if (g?.id != null) return String(g.id);
          const key = String(g?.nom || "")
            .trim()
            .toLowerCase();
          return nameToId.get(key);
        })
        .filter(Boolean)
    );

    setSelectedIds(initial);
  }, [isOpen, adherentGroups, nameToId]);

  // Liste triée + filtrée
  const sorted = useMemo(
    () =>
      (Array.isArray(groups) ? groups : [])
        .slice()
        .sort((a, b) =>
          String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
        ),
    [groups]
  );
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((g) =>
      String(g?.nom || "")
        .toLowerCase()
        .includes(q)
    );
  }, [sorted, filter]);

  const toggle = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return s;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);
    try {
      const current = new Set(
        (adherentGroups || [])
          .map((g) => {
            if (g?.id != null) return String(g.id);
            const key = String(g?.nom || "")
              .trim()
              .toLowerCase();
            return nameToId.get(key);
          })
          .filter(Boolean)
      );
      const selected = selectedIds;

      const toAdd = [...selected]
        .filter((id) => !current.has(id))
        .map((id) => Number(id));
      const toRemove = [...current]
        .filter((id) => !selected.has(id))
        .map((id) => Number(id));

      await Promise.all([
        ...toAdd.map((gid) => addAdherentToGroup(adherentId, gid)),
        ...toRemove.map((gid) => removeAdherentFromGroup(adherentId, gid)),
      ]);

      toast.success(
        `Groupes mis à jour (${toAdd.length} ajout(s), ${toRemove.length} retrait(s)).`
      );
      await refreshData?.();
      onClose?.();
    } catch (err) {
      console.error("Erreur mise à jour groupes :", err);
      toast.error("Erreur lors de la mise à jour des groupes.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Gérer les groupes
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Cochez / décochez les groupes pour cet adhérent.
          </p>
        </div>

        {/* Contenu scrollable */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 space-y-4 max-h-[70vh] overflow-auto">
            {/* Barre de recherche + compteur sélection */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Rechercher un groupe
                </label>
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Nom du groupe…"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Sélection(s)
                </label>
                <div className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-800 dark:text-slate-100 px-3 py-2">
                  {selectedIds.size} groupe(s)
                </div>
              </div>
            </div>

            {/* Liste des groupes (checkbox) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map((g) => {
                const checked = selectedIds.has(String(g.id));
                return (
                  <label
                    key={g.id}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition ring-1
                      ${
                        checked
                          ? "bg-violet-50 dark:bg-violet-950/30 border-violet-300/60 dark:border-violet-800/60 ring-violet-200/70 dark:ring-violet-800/70"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 ring-transparent"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(g.id)}
                      className="h-5 w-5 rounded-md border-slate-300 dark:border-slate-600 accent-violet-600"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {g.nom}
                      </div>
                      {g?.dojo_nom && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          Dojo : {g.dojo_nom}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}

              {filtered.length === 0 && (
                <div className="col-span-full text-sm text-slate-500 dark:text-slate-300">
                  Aucun groupe ne correspond à votre recherche.
                </div>
              )}
            </div>
          </div>

          {/* Footer d’actions */}
          <div className="flex justify-end gap-2 px-5 pb-5">
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={submitting}
              className={`!rounded-xl gap-2 ${
                submitting ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
